import React from "react";
import { motion } from "framer-motion";
import {
  Briefcase,
  Layers,
  Users,
  Code,
  Cloud,
  Brain,
  Globe,
  Award,
} from "lucide-react";
import { Link } from "react-router-dom";

export default function CorporateTraining() {
  return (
    <div className="w-full text-gray-800 font-sans">

      {/* ================= HERO ================= */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-700 text-white py-28 px-6"
      >
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-8">
              Corporate Training & Enterprise Upskilling
            </h1>

            <p className="text-lg md:text-xl text-blue-100 mb-10 max-w-xl">
              We design and deliver enterprise-grade corporate training programs
              that go beyond tools—focusing on architecture, scalability,
              real-world delivery, and long-term technology capability building.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="px-6 py-3 rounded-lg font-semibold
                           bg-white/20 backdrop-blur-md border border-white/30
                           hover:bg-white/30 transition"
              >
                Discuss Training Requirements
              </Link>
            </div>
          </div>

          <div className="hidden lg:block">
            <div className="space-y-5">
              {[
                "Consulting-led enterprise training",
                "Production-grade practices & architecture",
                "Hands-on delivery aligned to real systems",
                "Global delivery for distributed teams",
              ].map((item) => (
                <div key={item} className="flex gap-4 items-start">
                  <span className="text-blue-300 text-xl">›</span>
                  <p className="text-lg font-medium">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* ================= PHILOSOPHY ================= */}
      <section className="py-28 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-10">
            Our Corporate Training Philosophy
          </h2>

          <div className="grid lg:grid-cols-2 gap-16">
            <p className="text-gray-600 leading-relaxed text-lg">
              At TechX Incorporation, corporate training is not treated as a
              classroom activity. We approach training engagements with the same
              rigor as enterprise consulting assignments—starting from business
              objectives, system landscapes, and organizational maturity.
            </p>

            <p className="text-gray-600 leading-relaxed text-lg">
              Our programs are designed to help teams build sustainable
              capabilities, adopt best practices, and confidently work with
              complex enterprise systems in real production environments.
            </p>
          </div>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section className="py-28 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-20">
            How Our Corporate Training Engagements Work
          </h2>

          <div className="grid md:grid-cols-3 gap-10">
            <ProcessCard
              icon={Briefcase}
              title="Enterprise Assessment"
              desc="We begin by understanding business goals, team skill levels, existing technology stacks, and enterprise constraints."
            />
            <ProcessCard
              icon={Layers}
              title="Program Design"
              desc="Training programs are customized to your context, combining conceptual clarity, architecture discussions, and practical labs."
            />
            <ProcessCard
              icon={Users}
              title="Hands-on Delivery"
              desc="Delivery emphasizes real-world problem solving, production scenarios, and best practices aligned to enterprise delivery."
            />
          </div>
        </div>
      </section>

      {/* ================= TECHNOLOGY COVERAGE ================= */}
      <section className="py-28 px-6 bg-gray-900 text-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-10">
            Technology Coverage
          </h2>

          <p className="text-gray-300 max-w-4xl mb-20 leading-relaxed">
            Our corporate training portfolio spans modern engineering,
            cloud-native platforms, data systems, and enterprise technologies,
            delivered with strong emphasis on production readiness.
          </p>

          <div className="grid md:grid-cols-3 gap-10">
            <TechCard
              icon={Code}
              title="Engineering & Frameworks"
              desc="Java, Python, Node.js, TypeScript, Kotlin, Swift, Dart, Spring, Spring Boot, React, Angular, Playwright, Cypress, and mobile platforms."
            />
            <TechCard
              icon={Cloud}
              title="Cloud, Data & Platforms"
              desc="AWS, Azure, GCP, Docker, Kubernetes, Apache Spark, Airflow, NiFi, IOMETE, and modern data platforms."
            />
            <TechCard
              icon={Brain}
              title="AI, Databases & Enterprise Systems"
              desc="AI-driven systems, MongoDB, SQL databases, Cassandra, Redis, and Oracle enterprise platforms including VBCS and Visual Builder."
            />
          </div>
        </div>
      </section>

      {/* ================= DELIVERY MODELS ================= */}
      <section className="py-28 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-20">
            Training Delivery Models
          </h2>

          <div className="grid md:grid-cols-3 gap-10">
            <ProcessCard
              icon={Users}
              title="Onsite Corporate Training"
              desc="Instructor-led training delivered at client locations for immersive and focused learning."
            />
            <ProcessCard
              icon={Globe}
              title="Virtual Instructor-Led Training"
              desc="Live, interactive virtual training designed for distributed and global teams."
            />
            <ProcessCard
              icon={Award}
              title="Blended & Long-Term Programs"
              desc="Multi-phase training aligned with enterprise transformation initiatives and long-term roadmaps."
            />
          </div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="py-28 px-6 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Let’s Design a Corporate Training Program for Your Teams
          </h2>

          <p className="text-blue-100 mb-10 leading-relaxed">
            Engage with our consultants to discuss your training requirements,
            technology landscape, and long-term capability goals.
          </p>

          <Link
            to="/contact"
            className="inline-block px-8 py-4 rounded-lg font-semibold
                       bg-white/20 backdrop-blur-md border border-white/30
                       hover:bg-white/30 transition"
          >
            Request Consultation
          </Link>
        </div>
      </section>

    </div>
  );
}

/* ================= COMPONENTS ================= */

function ProcessCard({ icon: Icon, title, desc }) {
  return (
    <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition">
      <Icon className="text-blue-600 mb-4" size={28} />
      <h3 className="font-semibold text-xl mb-3">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{desc}</p>
    </div>
  );
}

function TechCard({ icon: Icon, title, desc }) {
  return (
    <div className="bg-gray-800 rounded-2xl p-8 hover:bg-gray-700 transition">
      <Icon className="text-blue-400 mb-4" size={28} />
      <h3 className="font-semibold text-xl mb-3 text-white">{title}</h3>
      <p className="text-gray-300 leading-relaxed">{desc}</p>
    </div>
  );
}
